import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import POS from './components/POS';
import Inventory from './components/Inventory';
import Reports from './components/Reports';
import Vendors from './components/Vendors';
import PurchaseOrders from './components/PurchaseOrders';
import ExpenditureModule from './components/Expenditure';
import UsersModule from './components/Users';
import Settings from './components/Settings';
import { ViewState, Product, CartItem, Vendor, PurchaseOrder, Expenditure, User, StoreSettings, Sale, UserRole } from './types';

// --- Data Sanitization Helpers ---
// These functions ensure that numeric data from the API is correctly typed as a number.
const sanitizeProduct = (product: any): Product => ({
  ...product,
  stripsPerPack: parseInt(String(product.stripsPerPack), 10) || 1,
  unitsPerStrip: parseInt(String(product.unitsPerStrip), 10) || 1,
  pricePack: parseFloat(String(product.pricePack)) || 0,
  priceStrip: parseFloat(String(product.priceStrip)) || 0,
  priceUnit: parseFloat(String(product.priceUnit)) || 0,
  cost: parseFloat(String(product.cost)) || 0,
  stock: parseInt(String(product.stock), 10) || 0,
  autoUpdatePricing: Boolean(product.autoUpdatePricing),
  batches: (product.batches || []).map((batch: any) => ({
    ...batch,
    quantity: parseInt(String(batch.quantity), 10) || 0,
    cost: parseFloat(String(batch.cost)) || 0,
  })),
});

const sanitizeVendor = (vendor: any): Vendor => ({
  ...vendor,
  payableAmount: parseFloat(String(vendor.payableAmount)) || 0,
  activeOrders: parseInt(String(vendor.activeOrders), 10) || 0,
});

const sanitizePurchaseOrder = (order: any): PurchaseOrder => ({
  ...order,
  totalCost: parseFloat(String(order.totalCost)) || 0,
  items: (order.items || []).map((item: any) => ({
    ...item,
    quantity: parseInt(String(item.quantity), 10) || 0,
    cost: parseFloat(String(item.cost)) || 0,
  })),
});

const sanitizeExpenditure = (exp: any): Expenditure => ({
  ...exp,
  amount: parseFloat(String(exp.amount)) || 0,
});

const sanitizeSale = (sale: any): Sale => ({
  ...sale,
  totalAmount: parseFloat(String(sale.totalAmount)) || 0,
  items: (sale.items || []).map((item: any) => ({
    ...item,
    quantity: parseInt(String(item.quantity), 10) || 0,
  })),
});


const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.DASHBOARD);
  const [loading, setLoading] = useState(true);

  // State slices for the entire application
  const [products, setProducts] = useState<Product[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [expenditures, setExpenditures] = useState<Expenditure[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [expCategories, setExpCategories] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<StoreSettings | null>(null);

  const apiRequest = async (action: string, payload?: any) => {
    try {
      const response = await fetch('https://brightbrains.pk/store/api.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, payload })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API Request failed for action: ${action}`, error);
      // Here you could add user-facing error notifications
      return { success: false, message: 'An unexpected error occurred.' };
    }
  };

  useEffect(() => {
    const checkAuth = () => {
        const loggedIn = sessionStorage.getItem('isAuthenticated');
        if (loggedIn) {
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
            setLoading(false);
        }
    };

    checkAuth();
  }, []);
  
  useEffect(() => {
     const fetchInitialData = async () => {
      setLoading(true);
      const result = await apiRequest('get_initial_data');
      if (result.success) {
        setProducts((result.data.products || []).map(sanitizeProduct));
        setVendors((result.data.vendors || []).map(sanitizeVendor));
        setPurchaseOrders((result.data.purchase_orders || []).map(sanitizePurchaseOrder));
        setExpenditures((result.data.expenditures || []).map(sanitizeExpenditure));
        setSales((result.data.sales || []).map(sanitizeSale));
        setExpCategories(result.data.exp_categories.map((c: any) => c.name) || []);
        setUsers(result.data.users || []);
        setSettings(result.data.settings ? {
          ...result.data.settings,
          taxRate: parseFloat(String(result.data.settings.taxRate)) || 0,
          lowStockThreshold: parseInt(String(result.data.settings.lowStockThreshold), 10) || 0,
        } : null);
      } else {
        console.error("Failed to fetch initial data:", result.message);
      }
      setLoading(false);
    };
    if(isAuthenticated) {
        fetchInitialData();
    }
  }, [isAuthenticated]);


  const handleLogin = () => {
      sessionStorage.setItem('isAuthenticated', 'true');
      setIsAuthenticated(true);
  };
  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    setIsAuthenticated(false);
    setCurrentView(ViewState.DASHBOARD);
  };

  // --- CRUD Handlers ---
  const handleSaleComplete = async (items: CartItem[], total: number, method: 'Cash' | 'Card' | 'Online') => {
    const newSale = {
      date: new Date().toISOString().split('T')[0],
      totalAmount: total,
      paymentMethod: method,
      items: items.map(item => ({...item, product_id: item.id})) // Ensure product_id for backend
    };

    const result = await apiRequest('add_sale', newSale);
    if(result.success) {
        setSales(prev => [sanitizeSale(result.data.sale), ...prev]);
        setProducts((result.data.updated_products || []).map(sanitizeProduct));
    }
  };
  
  const handleAddProduct = async (product: Omit<Product, 'id' | 'batches'>) => {
    const result = await apiRequest('add_product', product);
    if(result.success) setProducts(prev => [sanitizeProduct(result.data), ...prev]);
  }

  const handleEditProduct = async (product: Product) => {
    const result = await apiRequest('edit_product', product);
    if(result.success) setProducts(prev => prev.map(p => p.id === product.id ? sanitizeProduct(result.data) : p));
  }

  const handleDeleteProduct = async (id: string) => {
    if(!window.confirm("Are you sure you want to delete this product? This action cannot be undone.")) return;
    const result = await apiRequest('delete_product', {id});
    if(result.success) setProducts(prev => prev.filter(p => p.id !== id));
  }

  const handleAddVendor = async (vendor: Omit<Vendor, 'id'>) => {
    const result = await apiRequest('add_vendor', vendor);
    if(result.success) setVendors(prev => [sanitizeVendor(result.data), ...prev]);
  }

  const handleEditVendor = async (vendor: Vendor) => {
    const result = await apiRequest('edit_vendor', vendor);
    if(result.success) setVendors(prev => prev.map(v => v.id === vendor.id ? sanitizeVendor(result.data) : v));
  }

  const handleDeleteVendor = async (id: string) => {
    if(!window.confirm("Are you sure you want to delete this vendor?")) return;
    const result = await apiRequest('delete_vendor', {id});
    if(result.success) setVendors(prev => prev.filter(v => v.id !== id));
  }
  
  const handleCreateOrder = async (order: Omit<PurchaseOrder, 'id'>) => {
    const result = await apiRequest('add_purchase_order', order);
    if(result.success) setPurchaseOrders(prev => [sanitizePurchaseOrder(result.data), ...prev]);
  }

  const handleReceiveOrder = async (orderId: string, updates: any) => {
    const result = await apiRequest('receive_purchase_order', {orderId, updates});
    if(result.success) {
      setPurchaseOrders(prev => prev.map(o => o.id === orderId ? sanitizePurchaseOrder(result.data.order) : o));
      setProducts((result.data.products || []).map(sanitizeProduct));
      setVendors((result.data.vendors || []).map(sanitizeVendor));
    }
  }

  const handleCancelOrder = async (id: string) => {
    const result = await apiRequest('update_purchase_order_status', {id, status: 'Cancelled'});
    if(result.success) setPurchaseOrders(prev => prev.map(o => o.id === id ? sanitizePurchaseOrder(result.data) : o));
  }
  
  const handleAddExpenditure = async (exp: Omit<Expenditure, 'id'>) => {
    const result = await apiRequest('add_expenditure', exp);
    if(result.success) setExpenditures(prev => [sanitizeExpenditure(result.data), ...prev]);
  }
  
  const handleDeleteExpenditure = async (id: string) => {
    if(!window.confirm("Are you sure you want to delete this expenditure record?")) return;
    const result = await apiRequest('delete_expenditure', {id});
    if(result.success) setExpenditures(prev => prev.filter(e => e.id !== id));
  }
  
  const handleAddCategory = async (cat: string) => {
    const result = await apiRequest('add_expenditure_category', {name: cat});
    if(result.success) setExpCategories(prev => [...prev, cat]);
  }
  
  const handleDeleteCategory = async (cat: string) => {
    const result = await apiRequest('delete_expenditure_category', {name: cat});
    if(result.success) setExpCategories(prev => prev.filter(c => c !== cat));
  }
  
  const handleAddUser = async (user: Omit<User, 'id'>) => {
    const result = await apiRequest('add_user', user);
    if(result.success) setUsers(prev => [result.data, ...prev]);
  }
  
  const handleEditUser = async (user: User) => {
    const result = await apiRequest('edit_user', user);
    if(result.success) setUsers(prev => prev.map(u => u.id === user.id ? result.data : u));
  }
  
  const handleDeleteUser = async (id: string) => {
    if(!window.confirm("Are you sure you want to remove this user's access?")) return;
    const result = await apiRequest('delete_user', {id});
    if(result.success) setUsers(prev => prev.filter(u => u.id !== id));
  }

  const handleUpdateSettings = async (newSettings: StoreSettings) => {
    const result = await apiRequest('update_settings', newSettings);
    if(result.success) {
      setSettings({
        ...result.data,
        taxRate: parseFloat(String(result.data.taxRate)) || 0,
        lowStockThreshold: parseInt(String(result.data.lowStockThreshold), 10) || 0,
      });
    }
  }

  const renderContent = () => {
    if (loading) {
        return <div className="flex items-center justify-center h-full"><p>Loading Store Data...</p></div>;
    }

    switch (currentView) {
      case ViewState.DASHBOARD: return <Dashboard />;
      case ViewState.POS: return <POS products={products.filter(p=>p.stock > 0)} onCompleteSale={handleSaleComplete} />;
      case ViewState.INVENTORY: return <Inventory products={products} onAddProduct={handleAddProduct} onEditProduct={handleEditProduct} onDeleteProduct={handleDeleteProduct} />;
      case ViewState.PURCHASE: return <PurchaseOrders orders={purchaseOrders} vendors={vendors} products={products} onCreateOrder={handleCreateOrder} onReceiveOrder={handleReceiveOrder} onCancelOrder={handleCancelOrder} />;
      case ViewState.VENDORS: return <Vendors vendors={vendors} onAddVendor={handleAddVendor} onEditVendor={handleEditVendor} onDeleteVendor={handleDeleteVendor} />;
      case ViewState.EXPENDITURE: return <ExpenditureModule expenditures={expenditures} categories={expCategories} onAddCategory={handleAddCategory} onDeleteCategory={handleDeleteCategory} onAddExpenditure={handleAddExpenditure} onDeleteExpenditure={handleDeleteExpenditure} />;
      case ViewState.REPORTS: return <Reports sales={sales} purchases={purchaseOrders} expenditures={expenditures} products={products} vendors={vendors} />;
      case ViewState.USERS: return <UsersModule users={users} onAddUser={handleAddUser} onEditUser={handleEditUser} onDeleteUser={handleDeleteUser} />;
      case ViewState.SETTINGS: return settings ? <Settings settings={settings} onUpdateSettings={handleUpdateSettings} /> : <div>Loading settings...</div>;
      default: return <Dashboard />;
    }
  };

  if (!isAuthenticated) return <LoginPage onLogin={handleLogin} />;

  return (
    <Layout currentView={currentView} onNavigate={setCurrentView} onLogout={handleLogout} storeName={settings?.storeName} storePhone={settings?.phone} storeAddress={settings?.address}>
      {renderContent()}
    </Layout>
  );
};

export default App;